import React, { useState } from "react";
import { 
  Box, 
  Text, 
  VStack, 
  Card, 
  CardBody, 
  Heading,
  Center,
  List,
  ListItem,
  ListIcon,
  IconButton,
  Code,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { MdCheckCircle, MdBugReport } from "react-icons/md";
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';

export default function DemoAnalysisView({ conversationData }) {
  const analysisData = conversationData?.analysis?.evaluation;
  const rawData = conversationData;
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [showSegments, setShowSegments] = useState(false);

  // Helper function to extract just the transcript text
  const getTranscriptText = (transcriptData) => {
    if (!transcriptData) return '';
    // For raw transcript data
    if (transcriptData.transcription) {
      return transcriptData.transcription;
    }
    // For filtered transcript data
    if (transcriptData.result) {
      return transcriptData.result;
    }
    return '';
  };

  // Helper function to filter out segments from raw data
  const getFilteredRawData = (data) => {
    if (!data) return {};
    const filteredData = {...data};
    if (filteredData.raw_transcript_data?.segments) {
      filteredData.raw_transcript_data = {
        ...filteredData.raw_transcript_data,
        segments: '[Hidden - Click to Show]'
      };
    }
    return filteredData;
  };

  // Debug drawer component
  const DebugDrawer = () => (
    <Drawer
      isOpen={isDebugOpen}
      placement="right"
      onClose={() => setIsDebugOpen(false)}
      size="lg"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Debug Information</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Card>
              <CardBody>
                <Heading size="sm" mb={2}>Raw Conversation Data</Heading>
                <Button
                  size="sm"
                  leftIcon={showSegments ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  onClick={() => setShowSegments(!showSegments)}
                  variant="ghost"
                  mb={2}
                >
                  {showSegments ? 'Hide' : 'Show'} Segments
                </Button>
                <Code display="block" whiteSpace="pre" overflow="auto" p={2}>
                  {JSON.stringify(
                    showSegments ? rawData : getFilteredRawData(rawData), 
                    null, 
                    2
                  )}
                </Code>
              </CardBody>
            </Card>
            
            <Card>
              <CardBody>
                <Heading size="sm" mb={2}>Processed Analysis Data</Heading>
                <Code display="block" whiteSpace="pre" overflow="auto" p={2}>
                  {JSON.stringify(analysisData, null, 2)}
                </Code>
              </CardBody>
            </Card>

            {rawData?.raw_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>Raw Transcript</Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.raw_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}

            {rawData?.filtered_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>Filtered Transcript</Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.filtered_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  if (!analysisData) {
    return (
      <Center h="100vh">
        <Text>No analysis data available</Text>
      </Center>
    );
  }

  // Filter out the gameplan from regular evaluation items
  const evaluationItems = Object.entries(analysisData).filter(([key]) => key !== 'gameplan');

  return (
    <Box p={5} maxW="800px" mx="auto" position="relative">
      {/* Debug Button */}

      <Heading mb={6}>Conversation Analysis
      <IconButton
        icon={<MdBugReport />}
        position="fixed"
        right="4"
        top="4"
        colorScheme="gray"
        onClick={() => setIsDebugOpen(true)}
        aria-label="Debug View"
        title="Open Debug View"
      />
      
      
      </Heading>
      
      <VStack spacing={4} align="stretch">
        {/* Evaluation Cards */}
        {evaluationItems.map(([category, data]) => (
          <Card key={category} borderWidth="1px" borderRadius="lg" shadow="sm">
            <CardBody>
              <Heading size="md" mb={3} display="flex" alignItems="center" gap={2}>
                {data.score} {category.toUpperCase().replace(/_/g, ' ')}
              </Heading>
              
              {data.quote && (
                <Text fontStyle="italic" color="gray.600" mb={3}>
                  "{data.quote}"
                </Text>
              )}
              
              <Text mb={3}>{data.feedback}</Text>
              
              {data.example && (
                <Text color="green.600" fontWeight="medium">
                  Suggested approach: "{data.example}"
                </Text>
              )}
            </CardBody>
          </Card>
        ))}

        {/* Gameplan Section */}
        {analysisData.gameplan && (
          <Card borderWidth="1px" borderRadius="lg" shadow="sm" bg="blue.50">
            <CardBody>
              <Heading size="md" mb={3}>
                Action Plan
              </Heading>
              <List spacing={2}>
                {analysisData.gameplan.strategy.map((strategy, index) => (
                  <ListItem key={index} display="flex" alignItems="center">
                    <ListIcon as={MdCheckCircle} color="green.500" />
                    <Text>{strategy}</Text>
                  </ListItem>
                ))}
              </List>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Debug Drawer */}
      <DebugDrawer />
    </Box>
  );
}
